import React from 'react';

interface PlanDescriptionProps {
  planType: string;
  essentialImage: string;
  staffingImage: string;
  staffingPlan: string;
  boostedPlan: string;
}

interface EssentialPlanDescriptionProps {
  essentialImage: string;
}

interface StaffingPlanDescriptionProps {
  staffingImage: string;
  staffingPlan: string;
}

interface FullStaffingPlanDescriptionProps {
  staffingImage: string;
}

interface PriorityPlanDescriptionProps {
  staffingImage: string;
  boostedPlan: string;
  staffingPlan: string;
}

/**
 * PlanDescription Component - Matches original functionality from master branch
 * Displays different plan descriptions based on plan type
 */
const PlanDescription: React.FC<PlanDescriptionProps> = ({
  planType,
  essentialImage,
  staffingImage,
  staffingPlan,
  boostedPlan
}) => {
  // Check if planType includes string "Essential" (matches original logic)
  const isEssential = planType.includes('Essential');
  const isStaffing = planType.includes('Boosted');
  const isFullStaffing = planType.includes('Full');
  const isPriority = planType.includes('Priority');
  // Clinical / Non-Clinical membership (Essential successors). Order matters:
  // 'Non-Clinical' also contains 'Clinical'.
  const isNonClinical = planType.includes('Non-Clinical');
  const isClinical = planType.includes('Clinical') && !isNonClinical;

  return (
    <>
      {isEssential && (
        <EssentialPlanDescription essentialImage={essentialImage} />
      )}
      {(isClinical || isNonClinical) && (
        <SplitPlanDescription essentialImage={essentialImage} clinical={isClinical} />
      )}
      {isStaffing && (
        <StaffingPlanDescription 
          staffingImage={staffingImage} 
          staffingPlan={staffingPlan} 
        />
      )}
      {isFullStaffing && (
        <FullStaffingPlanDescription staffingImage={staffingImage} />
      )}
      {isPriority && (
        <PriorityPlanDescription 
          staffingImage={staffingImage} 
          boostedPlan={boostedPlan} 
          staffingPlan={staffingPlan} 
        />
      )}
    </>
  );
};

function SplitPlanDescription({ essentialImage, clinical }: { essentialImage: string, clinical: boolean }) {
  return (
    <>
      <ul className="list-group-flush">
        <li className="list-group-item pb-1">
          <div className="col-3 mb-3">
            <img className="img-fluid d-none d-xl-block" src={essentialImage} />
          </div>
          <div className="col-12 d-flex flex-column justify-content-center">
            <span id="essListHead" className="pb-2">
              What you get with a <u>{clinical ? 'Clinical' : 'Non-Clinical'} Membership</u>
            </span>
            {clinical ? (
              <p>
                The Clinical membership covers every role you hire for: hygienists,
                assistants, dentists, and specialists as well as front office,
                administration, lab, and management. Your jobs are also distributed to
                our partner job board network, and you get unlimited posts, full access
                to our database of dental professionals, temp booking, resumes, and
                messaging. Additional locations can be added at a reduced monthly price.
              </p>
            ) : (
              <p>
                The Non-Clinical membership covers assistants, front office,
                administration, lab, and management roles with unlimited posts, full
                access to our database of dental professionals, temp booking, resumes,
                and messaging. Temp shifts are included for every role, clinical ones
                too. Need a permanent hygienist, dentist, or specialist? Those are
                clinical roles; the Clinical membership covers them and adds partner
                job board distribution. Additional locations can be added at a reduced
                monthly price.
              </p>
            )}
          </div>
        </li>
      </ul>
    </>
  );
}

function EssentialPlanDescription({ essentialImage }: EssentialPlanDescriptionProps) {
  return (
    <>
      <ul className="list-group-flush">
        <li className="list-group-item pb-1">
          <div className="col-3 mb-3">
            <img
              className="img-fluid d-none d-xl-block"
              src={essentialImage}
            />
          </div>
          {/* Align items vertically center */}
          <div className="col-12 d-flex flex-column justify-content-center">
            <span id="essListHead" className="pb-2">What you get with an <u>Essential Membership</u></span>
            <p>
              The Essential membership is On Demand and designed to get you
              qualified full-time, part-time, or temporary dental staff
              quickly. A membership gives you 24/7 access to our rapidly
              growing network of 1,000,000+ dental professionals open to temporary
              and permanent work.
            </p>
          </div>
        </li>
        <li className="list-group-item">
          • View resumes and profiles online.
        </li>
        <li className="list-group-item">
          • Invite a candidate to interview via direct message.
        </li>
        <li className="list-group-item">
          • Book available dental temps using your smartphone.
        </li>
        <li className="list-group-item">
          • Recruit for unlimited job openings.
        </li>
        <li className="list-group-item">
          • Save big. No daily fees, no hefty percentages, and no
          cost to hire a temp permanently.
        </li>
      </ul>
    </>
  );
}

function StaffingPlanDescription({ staffingImage, staffingPlan }: StaffingPlanDescriptionProps) {
  return (
    <>
      <ul className="list-group-flush">
        <li className="list-group-item pb-1">
          <div className="col-3 mb-3">
            <img
              className="img-fluid d-none d-xl-block"
              src={staffingImage}
            />
          </div>
          {/* Align items vertically center */}
          <div className="col-12 d-flex flex-column justify-content-center">
            <span id="essListHead" className="pb-2">What you get with a <u>Boosted Plan</u></span>
            <p>For a one time fee of $599, we bring you candidates and you take it from there.</p>
          </div>
        </li>
        <ul className="list-group-flush">
          <li className="list-group-item">
            &#8226; Dedicated Recruiter to bring you qualified candidates for 30 days.
          </li>
          <li className="list-group-item">
            &#8226; 100x more visibility than a standard recruiting.
          </li>
          <li className="list-group-item">
            &#8226; More applicants for your job.
          </li>
          <li className="list-group-item">
            &#8226; An expertly-crafted job description to attract the best
            talent.
          </li>
          <li className="list-group-item">
            &#8226; Featured employer profile so candidates are attracted to
            your office.
          </li>
        </ul>
        <li className="list-group-item pb-1">
          <div className="col-12 d-flex flex-column justify-content-center">
            <span id="essListHead" className="pb-2">
              Want Even More Candidates?
            </span>
            <p>Our Full Staffing Service offers concierge-level service that includes even more candidates, candidate screening, a dedicated recruiter, and more.</p>
          </div>
          <div className="col-12 d-flex flex-row justify-content-between">
            <a href={`/plans/${staffingPlan}`} className="text-decoration-none">View Full Service Staffing Plan</a>
          </div>
        </li>
      </ul>
    </>
  );
}

function FullStaffingPlanDescription({ staffingImage }: FullStaffingPlanDescriptionProps) {
  return (
    <>
      <ul className="list-group-flush">
        <li className="list-group-item pb-1">
          <div className="col-3 mb-3">
            <img
              className="img-fluid d-none d-xl-block"
              src={staffingImage}
            />
          </div>
          {/* Align items vertically center */}
          <div className="col-12 d-flex flex-column justify-content-center">
            <span id="essListHead" className="pb-2">What you get with <u>Full Service Staffing</u></span>
            <p>This is concierge-level service so you won't lift a finger beyond
              giving us a call. Everything above is included for 30 days for a
              one time payment of $1299.</p>
          </div>
        </li>
        <ul className="list-group-flush">
          <li className="list-group-item">
            &#8226; Dedicated Recruiter to bring you qualified candidates for 30 days.
          </li>
          <li className="list-group-item">
            &#8226; 100x more visibility than a standard recruiting.
          </li>
          <li className="list-group-item">
            &#8226; More applicants for your job.
          </li>
          <li className="list-group-item">
            &#8226; An expertly-crafted job description to attract the best
            talent.
          </li>
          <li className="list-group-item">
            &#8226; Featured employer profile so candidates are attracted to
            your office.
          </li>
          <li className="list-group-item fw-bold">
            &#8226; Candidate screening & License verification.
          </li>
          <li className="list-group-item fw-bold">
            &#8226; Assist with finding temp staff as needed.
          </li>
        </ul>
      </ul>
    </>
  );
}

function PriorityPlanDescription({ staffingImage, boostedPlan, staffingPlan }: PriorityPlanDescriptionProps) {
  return (
    <>
      <ul className="list-group-flush">
        <li className="list-group-item pb-1">
          <div className="col-3 mb-3">
            <img
              className="img-fluid d-none d-xl-block"
              src={staffingImage}
            />
          </div>
          {/* Align items vertically center */}
          <div className="col-12 d-flex flex-column justify-content-center">
            <span id="essListHead" className="pb-2">What you get with a <u>Priority Job</u></span>
            <p>Need to hire faster? A priority job can help find you candidates faster by jumping to the front of the line with more promotion than a standard job posting.</p>
          </div>
        </li>
        <ul className="list-group-flush">
          <li className="list-group-item">
            &#8226; Prioritized Job Posting for 10 Days
          </li>
          <li className="list-group-item">
            &#8226; 10x more visibility than a standard recruiting.
          </li>
          <li className="list-group-item">
            &#8226; Jobs are cross-promoted on social media.
          </li>
        </ul>
        <li className="list-group-item pb-1">
          <div className="col-12 d-flex flex-column justify-content-center">
            <span id="essListHead" className="pb-2">
              Want Even More Candidates?
            </span>
            <p>Our Staffing Services offer a higher level of service that includes more candidates, candidate screening, a dedicated recruiter, and more.</p>
          </div>
          <div className="col-12 d-flex flex-column justify-content-between">
            <a href={`/plans/${boostedPlan}`} className="text-decoration-none">View Boosted Plan</a>
            <a href={`/plans/${staffingPlan}`} className="text-decoration-none">View Full Service Staffing Plan</a>
          </div>
        </li>
      </ul>
    </>
  );
}

export default PlanDescription;
